import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
    Check,
    Copy,
    Download,
    FileText,
    Image as ImageIcon,
    Video,
    Sparkles,
    ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";

const PLATFORM_META = {
    tiktok: {
        label: "TikTok",
        accent: "#FF2C55",
        accentGradient:
            "linear-gradient(180deg, #FF2C55 0%, #FF2C55 35%, #00F5D4 100%)",
        chip: "bg-[#FF2C55]/15 text-[#FF2C55] border-[#FF2C55]/30",
    },
    instagram: {
        label: "Instagram",
        accent: "#F77737",
        accentGradient:
            "linear-gradient(180deg, #F77737 0%, #E1306C 50%, #833AB4 100%)",
        chip: "bg-[#F77737]/15 text-[#F77737] border-[#F77737]/30",
    },
    facebook: {
        label: "Facebook",
        accent: "#5B8DEF",
        accentGradient:
            "linear-gradient(180deg, #5B8DEF 0%, #2F58CD 100%)",
        chip: "bg-[#5B8DEF]/15 text-[#5B8DEF] border-[#5B8DEF]/30",
    },
};

const KIND_ICON = { video: Video, image: ImageIcon, text: FileText };

async function writeClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        return false;
    }
}

export default function ConfirmationPanel({ confirmation, onAnother }) {
    const [downloading, setDownloading] = useState(false);

    const when = new Date(confirmation.scheduled_for);
    const formattedWhen = (() => {
        try {
            return format(when, "EEEE, MMM d · h:mm a");
        } catch {
            return confirmation.scheduled_for;
        }
    })();

    const kind = confirmation.asset_kind || "text";
    const isText = kind === "text";
    const KindIcon = KIND_ICON[kind] || FileText;

    const hashtagsLine = (confirmation.hashtags || []).join(" ");
    const captionPayload = hashtagsLine
        ? `${confirmation.caption}\n\n${hashtagsLine}`
        : confirmation.caption;

    const handleCopyCaption = async (platformLabel) => {
        const ok = await writeClipboard(captionPayload);
        if (ok) {
            toast.success(`Copied for ${platformLabel}`, {
                description: "Caption & hashtags on clipboard.",
            });
        } else {
            toast.error("Copy failed", {
                description: "Long-press to copy manually.",
            });
        }
    };

    const handleCopyTitle = async (platformLabel) => {
        const ok = await writeClipboard(confirmation.title);
        if (ok) {
            toast.success(`Title copied for ${platformLabel}`, {
                description: confirmation.title,
            });
        } else {
            toast.error("Copy failed", {
                description: "Long-press to copy manually.",
            });
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await api.get(
                `/assets/${confirmation.asset_id}/download`,
                { responseType: "blob" }
            );
            const cd = res.headers["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/.exec(cd);
            const filename = match
                ? match[1]
                : isText
                  ? "asset.txt"
                  : "asset";
            const mime = res.headers["content-type"] || "application/octet-stream";
            const blob = new Blob([res.data], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("Download started", { description: filename });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || err?.message || "Download failed";
            toast.error("Download failed", { description: String(detail) });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-6 rounded-2xl border border-[#1F2A40] bg-[#111726] p-6 sm:p-8"
            data-testid="confirmation-panel"
        >
            {/* Header */}
            <div className="flex items-start gap-4">
                <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10"
                    style={{ boxShadow: "0 0 28px rgba(0,245,212,0.25)" }}
                >
                    <Check
                        className="h-6 w-6 text-[#00F5D4]"
                        strokeWidth={3}
                        data-testid="confirmation-check-icon"
                    />
                </motion.div>
                <div className="flex flex-col gap-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#00F5D4]">
                        ready to copy
                    </span>
                    <h2
                        className="text-2xl font-medium tracking-tight text-[#E8ECF5]"
                        data-testid="confirmation-headline"
                    >
                        Approved &amp; ready to publish manually
                    </h2>
                    <p
                        className="font-mono text-sm text-[#8A93A6]"
                        data-testid="confirmation-when"
                    >
                        Planned for {formattedWhen}
                    </p>
                </div>
            </div>

            {/* Variant strip — one per platform */}
            <div className="flex flex-col gap-3" data-testid="variant-strip">
                {confirmation.platforms.map((p) => {
                    const meta = PLATFORM_META[p] || PLATFORM_META.facebook;
                    return (
                        <motion.article
                            key={p}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                                duration: 0.4,
                                delay: 0.2,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="group relative overflow-hidden rounded-xl border border-[#1F2A40] bg-[#0B0F19] pl-4"
                            data-testid={`variant-${p}`}
                        >
                            {/* Left accent bar */}
                            <span
                                aria-hidden
                                className="absolute inset-y-0 left-0 w-1"
                                style={{ background: meta.accentGradient }}
                            />
                            <div className="flex flex-col gap-3 px-5 py-5">
                                {/* Platform badge */}
                                <div className="flex items-center gap-2">
                                    <span
                                        className={[
                                            "rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em]",
                                            meta.chip,
                                        ].join(" ")}
                                        data-testid={`variant-badge-${p}`}
                                    >
                                        {meta.label}
                                    </span>
                                </div>
                                {/* Title */}
                                <h3
                                    className="text-lg font-medium leading-snug text-[#E8ECF5] sm:text-xl"
                                    data-testid={`variant-title-${p}`}
                                >
                                    {confirmation.title}
                                </h3>
                                {/* Caption */}
                                <p
                                    className="whitespace-pre-wrap text-sm leading-relaxed text-[#E8ECF5]/85"
                                    data-testid={`variant-caption-${p}`}
                                >
                                    {confirmation.caption}
                                </p>
                                {/* Hashtag chips */}
                                {confirmation.hashtags?.length > 0 && (
                                    <div
                                        className="flex flex-wrap gap-1.5"
                                        data-testid={`variant-hashtags-${p}`}
                                    >
                                        {confirmation.hashtags.map((h, i) => (
                                            <span
                                                key={`${p}-${i}`}
                                                className="rounded-full bg-[#1A2235] px-2.5 py-0.5 font-mono text-[11px] text-[#8A93A6]"
                                            >
                                                {h}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {/* Action row */}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleCopyCaption(meta.label)}
                                        className="group/copy relative inline-flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-[#00F5D4] to-[#00F5D4] px-4 py-2 text-xs font-semibold text-[#0B0F19] transition-all hover:shadow-[0_0_22px_rgba(0,245,212,0.5)]"
                                        data-testid={`copy-caption-btn-${p}`}
                                    >
                                        <Copy className="h-3.5 w-3.5" />
                                        Copy Caption &amp; Hashtags
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleCopyTitle(meta.label)}
                                        className="inline-flex items-center gap-2 rounded-lg border border-[#1F2A40] bg-transparent px-3 py-2 text-xs text-[#8A93A6] transition-colors hover:border-[#00F5D4]/40 hover:text-[#E8ECF5]"
                                        data-testid={`copy-title-btn-${p}`}
                                    >
                                        <Copy className="h-3 w-3" />
                                        Copy Title
                                    </button>
                                </div>
                            </div>
                        </motion.article>
                    );
                })}
            </div>

            {/* Download row */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#1F2A40]/80 bg-[#0B0F19] px-4 py-3">
                <div className="flex items-center gap-3">
                    <KindIcon className="h-4 w-4 text-[#8A93A6]" />
                    <span className="font-mono text-xs text-[#8A93A6]">
                        Source asset · {kind}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#2F58CD]/40 bg-transparent px-4 py-2 text-xs font-medium text-[#5B8DEF] transition-all hover:border-[#2F58CD] hover:bg-[#2F58CD]/10 hover:text-[#E8ECF5] disabled:cursor-not-allowed disabled:opacity-40"
                    data-testid="download-asset-btn"
                >
                    <Download className="h-3.5 w-3.5" />
                    {downloading ? "Preparing…" : isText ? "Download Text" : "Download Media"}
                </button>
            </div>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#1F2A40]/60 pt-4">
                <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[#00F5D4] transition-colors hover:underline"
                    data-testid="view-in-library-link"
                >
                    View in Library
                    <ArrowRight className="h-3 w-3" />
                </Link>
                <button
                    type="button"
                    onClick={onAnother}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-[#8A93A6] transition-colors hover:text-[#E8ECF5]"
                    data-testid="orchestrate-another-btn"
                >
                    <Sparkles className="h-3.5 w-3.5" />
                    Orchestrate another
                </button>
            </div>
        </motion.section>
    );
}
